// useTimelineScheduleEdit.js
//
// Day-level move/resize for Timeline Gantt bars with FS/SS domino cascade.

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { useUser } from 'context/UserContext';
import { updateNode } from 'services/nodes/NodeWriteService';
import { updateTaskScheduleWithCascade } from 'services/nodes/ScheduleCascadeService';
import { timestampFromDate } from 'utilities/DateTime';
import { planScheduleCascade } from 'schedule/dependencies/planScheduleCascade';
import { resolveTaskPredecessorLinks } from 'schedule/dependencies/predecessorLinks';
import {
  getCalendarFieldSectionData,
  getCalendarFieldStorageKey,
} from 'views/calendar/shared/calendarFieldUpdate';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';

const DRAG_THRESHOLD_PX = 8;

const toDayStart = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const toRangeMs = (range) => {
  if (!range?.start || !range?.end) return null;
  const start = toDayStart(new Date(range.start)).getTime();
  const end = toDayStart(new Date(range.end)).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end };
};

const shiftDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return toDayStart(next);
};

const buildRangeValue = (startDate, endDate, previous = {}) => ({
  ...previous,
  startDate: timestampFromDate(toDayStart(startDate)),
  endDate: timestampFromDate(toDayStart(endDate)),
  startPrecision: previous?.startPrecision || 'day',
  endPrecision: previous?.endPrecision || 'day',
  isRange: true,
});

const resolveNodeStatus = (node, typeDef) => {
  if (!node) return null;
  const statusField = (typeDef?.schema?.fields || []).find((field) => {
    const key = String(field?.name || field?.key || '').trim().toLowerCase();
    return key === 'status';
  });
  if (statusField) {
    return resolveSchemaFieldValue({
      item: node,
      fieldDef: statusField,
      typeKey: typeDef?.key,
      dataSection: typeDef?.dataSection,
    });
  }
  const typeKey = String(typeDef?.key || node.type || '').trim().toLowerCase();
  const section = typeKey ? node[`data_${typeKey}`] : null;
  return section?.status ?? node.status ?? null;
};

const buildTasksByKey = (entries, typeDef) => {
  const map = {};
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const key = String(entry?.key || entry?.node?.key || '').trim();
    if (!key || !entry?.node) return;
    const links = Array.isArray(entry.predecessorLinks) && entry.predecessorLinks.length
      ? entry.predecessorLinks
      : resolveTaskPredecessorLinks(entry.node, {
        typeKey: typeDef?.key,
        dataSection: typeDef?.dataSection,
      });
    map[key] = {
      schedule: entry.range || null,
      predecessorLinks: links,
      status: resolveNodeStatus(entry.node, typeDef),
      writable: true,
      node: entry.node,
    };
  });
  return map;
};

/**
 * @param {object} params
 * @param {object|null} params.typeDef
 * @param {object|null} params.timelineFieldDef
 * @param {boolean} params.enabled
 * @param {number} params.pxPerDay
 * @param {Record<string, { start: Date, end: Date }>} [params.liveRangesByKey]
 * @param {object[]} [params.entries]
 */
export default function useTimelineScheduleEdit({
  typeDef = null,
  timelineFieldDef = null,
  enabled = false,
  pxPerDay = 88,
  liveRangesByKey = null,
  entries = [],
} = {}) {
  const { user } = useUser();
  const dragRef = useRef(null);
  const previewRef = useRef(null);
  const suppressClickRef = useRef(false);
  const pendingRef = useRef(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [optimisticByKey, setOptimisticByKey] = useState({});
  const [cascadeConfirmOpen, setCascadeConfirmOpen] = useState(false);
  const [cascadeConfirmMessage, setCascadeConfirmMessage] = useState('');
  const [isCommitting, setIsCommitting] = useState(false);

  useEffect(() => {
    if (!liveRangesByKey || typeof liveRangesByKey !== 'object') return;

    setOptimisticByKey((prev) => {
      const keys = Object.keys(prev);
      if (!keys.length) return prev;

      let changed = false;
      const next = { ...prev };
      keys.forEach((key) => {
        const optimistic = prev[key];
        const liveMs = toRangeMs(liveRangesByKey[key]);
        const optimisticMs = toRangeMs(optimistic);
        if (!liveMs || !optimisticMs) return;
        if (liveMs.start === optimisticMs.start && liveMs.end === optimisticMs.end) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [liveRangesByKey]);

  const consumeClickSuppression = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }, []);

  const applyOptimisticWrites = useCallback((writes) => {
    const next = {};
    (writes || []).forEach((write) => {
      if (!write?.taskKey || !write?.schedule?.start || !write?.schedule?.end) return;
      next[write.taskKey] = {
        start: write.schedule.start,
        end: write.schedule.end,
      };
    });
    setOptimisticByKey((prev) => ({ ...prev, ...next }));
  }, []);

  const persistWrites = useCallback(async (writes, tasksByKey, mode) => {
    const fieldName = timelineFieldDef?.name || timelineFieldDef?.key;
    const typeKey = typeDef?.key || '';
    if (!fieldName) throw new Error('Timeline field unavailable.');

    const rootWrite = (writes || []).find((write) => tasksByKey[write.taskKey]?.node);
    const rootNode = rootWrite ? tasksByKey[rootWrite.taskKey]?.node : null;
    const baseKey = String(rootNode?.baseKey || '').trim();
    const changedTaskKey = rootWrite?.taskKey || '';

    if (baseKey && changedTaskKey && typeKey && rootWrite) {
      try {
        await updateTaskScheduleWithCascade({
          baseKey,
          changedTaskKey,
          typeKey,
          fieldName,
          mode,
          candidateKeys: Object.keys(tasksByKey),
          nextSchedule: rootWrite.schedule,
        });
        return;
      } catch (error) {
        console.warn('[Timeline] cascade callable failed; falling back to client writes:', error);
      }
    }

    await Promise.all((writes || []).map(async (write) => {
      const task = tasksByKey[write.taskKey];
      const node = task?.node;
      if (!node?.key) return;

      const storageKey = getCalendarFieldStorageKey(node, timelineFieldDef, typeKey || node.type);
      if (!storageKey) return;

      const sectionData = getCalendarFieldSectionData(node, storageKey) || {};
      const previousValue = sectionData?.[fieldName];
      const nextValue = buildRangeValue(
        write.schedule.start,
        write.schedule.end,
        previousValue || {},
      );

      await updateNode(node.key, {
        [storageKey]: {
          ...sectionData,
          [fieldName]: nextValue,
        },
      }, { actor: user });
    }));
  }, [timelineFieldDef, typeDef, user]);

  const clearPending = useCallback(() => {
    pendingRef.current = null;
    setCascadeConfirmOpen(false);
    setCascadeConfirmMessage('');
  }, []);

  const commitPending = useCallback(async (mode) => {
    const pending = pendingRef.current;
    if (!pending || isCommitting) return;

    const plan = mode === 'pin-successors' ? pending.pinPlan : pending.pushPlan;
    setIsCommitting(true);
    applyOptimisticWrites(plan.writes);

    try {
      await persistWrites(plan.writes, pending.tasksByKey, mode);
      if (mode === 'push' && plan.affectedCount > 0) {
        toast.success(
          plan.affectedCount === 1
            ? 'Schedule updated (1 dependent shifted).'
            : `Schedule updated (${plan.affectedCount} dependents shifted).`,
        );
      }
      clearPending();
    } catch (error) {
      setOptimisticByKey({});
      console.error('[Timeline] Failed to update schedule:', error);
      toast.error(error?.message || 'Could not update schedule.');
      clearPending();
    } finally {
      setIsCommitting(false);
    }
  }, [applyOptimisticWrites, clearPending, isCommitting, persistWrites]);

  const handleCascadeConfirm = useCallback(() => {
    commitPending('push');
  }, [commitPending]);

  const handleCascadePin = useCallback(() => {
    commitPending('pin-successors');
  }, [commitPending]);

  const handleCascadeCancel = useCallback(() => {
    setOptimisticByKey({});
    clearPending();
  }, [clearPending]);

  const beginDrag = useCallback((event, entry, mode = 'move', options = {}) => {
    if (!enabled || cascadeConfirmOpen || isCommitting) return;
    if (!entry?.node?.key || !entry?.range?.start || !entry?.range?.end) return;
    if (!timelineFieldDef?.name && !timelineFieldDef?.key) return;
    if (event.button != null && event.button !== 0) return;

    // Don't preventDefault here — that would kill the subsequent click for Peek.
    // Drag only arms after the pointer moves past DRAG_THRESHOLD_PX.
    event.stopPropagation();

    const originStart = toDayStart(new Date(entry.range.start));
    const originEnd = toDayStart(new Date(entry.range.end));
    const onPressClick = typeof options?.onPressClick === 'function'
      ? options.onPressClick
      : null;

    suppressClickRef.current = false;
    dragRef.current = {
      mode,
      entry,
      startClientX: event.clientX,
      originStart,
      originEnd,
      active: false,
      onPressClick,
    };
    previewRef.current = null;

    const activateDrag = (clientX) => {
      const drag = dragRef.current;
      if (!drag || drag.active) return;
      drag.active = true;
      const initialPreview = {
        key: entry.key,
        start: originStart,
        end: originEnd,
      };
      previewRef.current = initialPreview;
      setDragPreview(initialPreview);
      try {
        event.target?.setPointerCapture?.(event.pointerId);
      } catch {
        // Ignore capture failures (e.g. non-Element targets).
      }
      // Recompute once so the first move past threshold applies.
      handleMove({ clientX });
    };

    const handleMove = (moveEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const deltaPx = moveEvent.clientX - drag.startClientX;

      if (!drag.active) {
        if (Math.abs(deltaPx) < DRAG_THRESHOLD_PX) return;
        activateDrag(moveEvent.clientX);
        return;
      }

      const dayDelta = Math.round(deltaPx / Math.max(1, pxPerDay));

      let nextStart = drag.originStart;
      let nextEnd = drag.originEnd;

      if (drag.mode === 'move') {
        nextStart = shiftDays(drag.originStart, dayDelta);
        nextEnd = shiftDays(drag.originEnd, dayDelta);
      } else if (drag.mode === 'resize-start') {
        nextStart = shiftDays(drag.originStart, dayDelta);
        if (nextStart.getTime() > drag.originEnd.getTime()) {
          nextStart = new Date(drag.originEnd);
        }
      } else if (drag.mode === 'resize-end') {
        nextEnd = shiftDays(drag.originEnd, dayDelta);
        if (nextEnd.getTime() < drag.originStart.getTime()) {
          nextEnd = new Date(drag.originStart);
        }
      }

      const nextPreview = {
        key: drag.entry.key,
        start: nextStart,
        end: nextEnd,
      };
      previewRef.current = nextPreview;
      setDragPreview(nextPreview);
    };

    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);

      const drag = dragRef.current;
      const preview = previewRef.current;
      const didDrag = Boolean(drag?.active);
      const datesChanged = Boolean(
        didDrag
        && drag
        && preview
        && preview.key === drag.entry.key
        && (
          preview.start.getTime() !== drag.originStart.getTime()
          || preview.end.getTime() !== drag.originEnd.getTime()
        ),
      );

      dragRef.current = null;
      previewRef.current = null;
      setDragPreview(null);

      // Click / cancelled drag — allow the following click event to open Peek.
      if (!datesChanged) {
        suppressClickRef.current = false;
        // Open Peek from pointerup when this was a press (not a drag). More
        // reliable than waiting on click, which some pointer paths skip.
        if (!didDrag && typeof drag?.onPressClick === 'function') {
          suppressClickRef.current = true;
          drag.onPressClick();
        }
        return;
      }

      // Real schedule change — swallow the trailing click.
      suppressClickRef.current = true;

      const fieldName = timelineFieldDef?.name || timelineFieldDef?.key;
      const typeKey = typeDef?.key || drag.entry.node?.type || '';
      const storageKey = getCalendarFieldStorageKey(
        drag.entry.node,
        timelineFieldDef,
        typeKey,
      );
      if (!storageKey || !fieldName) {
        toast.error('Could not update schedule: field storage unavailable.');
        return;
      }

      const tasksByKey = buildTasksByKey(entries, typeDef);
      // Ensure dragged node is present even if filtered out of entries
      if (!tasksByKey[drag.entry.key]) {
        tasksByKey[drag.entry.key] = {
          schedule: drag.entry.range,
          predecessorLinks: drag.entry.predecessorLinks
            || resolveTaskPredecessorLinks(drag.entry.node, {
              typeKey: typeDef?.key,
              dataSection: typeDef?.dataSection,
            }),
          status: resolveNodeStatus(drag.entry.node, typeDef),
          writable: true,
          node: drag.entry.node,
        };
      }

      const nextSchedule = { start: preview.start, end: preview.end };
      const pushPlan = planScheduleCascade({
        changedTaskKey: drag.entry.key,
        nextSchedule,
        tasksByKey,
        mode: 'push',
      });
      const pinPlan = planScheduleCascade({
        changedTaskKey: drag.entry.key,
        nextSchedule,
        tasksByKey,
        mode: 'pin-successors',
      });

      applyOptimisticWrites(pushPlan.writes);

      if (pushPlan.affectedCount <= 0) {
        pendingRef.current = { pushPlan, pinPlan, tasksByKey };
        commitPending('push');
        return;
      }

      pendingRef.current = { pushPlan, pinPlan, tasksByKey };
      setCascadeConfirmMessage(
        pushPlan.affectedCount === 1
          ? 'This change affects 1 dependent task. Shift it to keep links valid?'
          : `This change affects ${pushPlan.affectedCount} dependent tasks. Shift them to keep links valid?`,
      );
      setCascadeConfirmOpen(true);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
  }, [
    applyOptimisticWrites,
    cascadeConfirmOpen,
    commitPending,
    enabled,
    entries,
    isCommitting,
    pxPerDay,
    timelineFieldDef,
    typeDef,
  ]);

  const applyPreviewRange = useCallback((entry) => {
    const entryKey = entry?.key;
    if (!entryKey) return entry?.range || null;

    if (dragPreview?.key === entryKey) {
      return {
        ...entry.range,
        start: dragPreview.start,
        end: dragPreview.end,
        isMilestone: dragPreview.start.getTime() === dragPreview.end.getTime(),
      };
    }

    const optimistic = optimisticByKey[entryKey];
    if (optimistic?.start && optimistic?.end) {
      return {
        ...entry.range,
        start: optimistic.start,
        end: optimistic.end,
        isMilestone: optimistic.start.getTime() === optimistic.end.getTime(),
      };
    }

    return entry?.range || null;
  }, [dragPreview, optimisticByKey]);

  return {
    beginDrag,
    applyPreviewRange,
    consumeClickSuppression,
    dragPreviewKey: dragPreview?.key || null,
    cascadeConfirmOpen,
    cascadeConfirmMessage,
    cascadeConfirmBusy: isCommitting,
    handleCascadeConfirm,
    handleCascadePin,
    handleCascadeCancel,
    setCascadeConfirmOpen: (open) => {
      if (!open) handleCascadeCancel();
      else setCascadeConfirmOpen(true);
    },
  };
}
